import "rsuite/dist/rsuite.css";
import React, { useState, useEffect } from 'react';
import NavigationBar from '../NavigationBar';
import axios from '../axios';
import { Container, Row, Col, Table, Form, Button } from 'react-bootstrap';
import { QRCode } from 'antd';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { renderPagination, renderAdvancedPagination, formatNumberWithCommas } from '../utilities';
import '../Functions/manage_orders.css';

const todayBd = () => new Date(Date.now() + 6 * 3600 * 1000).toISOString().slice(0, 10);

function RollCodes() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    // Opened from a receipt ("Print labels"): show just that truck's rolls
    const [receiptId, setReceiptId] = useState(searchParams.get('receiptId') || '');
    const [receivedDate, setReceivedDate] = useState('');
    const [needsReprint, setNeedsReprint] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [rolls, setRolls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [papers, setPapers] = useState([]);
    const [gsmId, setGsmId] = useState('');
    const [sizes, setSizes] = useState([]);
    const [rollSize, setRollSize] = useState('');
    const [status, setStatus] = useState('Active');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [totalKg, setTotalKg] = useState(0);
    const [activeRows, setActiveRows] = useState(0);
    // Roll codes ticked for "Print selected" - kept across pages and filter changes
    const [selected, setSelected] = useState([]);
    // Rolls currently rendered as printable labels (one QR label per A4 page)
    const [labels, setLabels] = useState([]);
    const [preparingPrint, setPreparingPrint] = useState(false);

    useEffect(() => {
        axios.get(`${window.backendUrl}/get_roll_code_papers`)
            .then((res) => setPapers(res.data.data || []))
            .catch((error) => console.error("Error fetching papers:", error));
    }, []);

    // Sizes that have active rolls, within the chosen paper
    useEffect(() => {
        axios.get(`${window.backendUrl}/get_roll_code_sizes`, { params: { gsmId: gsmId || undefined } })
            .then((res) => setSizes(res.data.data || []))
            .catch((error) => console.error("Error fetching roll sizes:", error));
    }, [gsmId]);

    // Search waits until typing pauses
    useEffect(() => {
        const timer = setTimeout(() => { setSearch(searchInput.trim()); setCurrentPage(1); }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const filterParams = () => ({
        gsmId: gsmId || undefined,
        rollSize: rollSize === '' ? undefined : rollSize,
        search: search || undefined,
        receiptId: receiptId || undefined,
        receivedDate: receivedDate || undefined,
        needsReprint: needsReprint ? '1' : undefined,
    });

    // Fetch Data
    useEffect(() => {
        fetchRollCodes(currentPage);
    }, [currentPage, gsmId, rollSize, status, search, receiptId, receivedDate, needsReprint]);

    // Once the labels are on the page, open the browser print dialog
    useEffect(() => {
        if (!labels.length) return;
        const timer = setTimeout(() => {
            window.print();
            setLabels([]);
        }, 300);
        return () => clearTimeout(timer);
    }, [labels]);

    const fetchRollCodes = async (page) => {
        setLoading(true);
        try {
            const response = await axios.get(`${window.backendUrl}/get_roll_codes`, {
                params: { page: page, ...filterParams(), status: status || undefined }
            });

            if (response.data) {
                setRolls(response.data.data);
                setCurrentPage(response.data.currentPage);
                setTotalPages(Math.max(1, response.data.totalPages));
                setTotalRows(response.data.totalRows || 0);
                setTotalKg(Number(response.data.totalKg) || 0);
                setActiveRows(response.data.activeRows || 0);
            }
        } catch (error) {
            console.error("Error fetching roll codes:", error);
        }
        setLoading(false);
    };

    // Print labels for specific rolls (codes), or for every active roll matching the filters
    const printLabels = async (codes, params) => {
        setPreparingPrint(true);
        try {
            const response = await axios.get(`${window.backendUrl}/get_roll_labels`, {
                params: codes ? { rollCodes: codes.join(',') } : (params || filterParams())
            });
            const data = response.data.data || [];
            if (!data.length) alert('No rolls to print');
            setLabels(data);
        } catch (error) {
            console.error("Error fetching roll labels:", error);
            alert('Failed to load labels');
        }
        setPreparingPrint(false);
    };

    const toggleSelected = (code) => setSelected((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
    const pageCodes = rolls.map((r) => r.rollCode);
    const allPageSelected = pageCodes.length > 0 && pageCodes.every((c) => selected.includes(c));
    const togglePage = () => setSelected((prev) => (allPageSelected
        ? prev.filter((c) => !pageCodes.includes(c))
        : [...prev, ...pageCodes.filter((c) => !prev.includes(c))]));

    const filtersOn = !!(gsmId || rollSize !== '' || search || receiptId || receivedDate || needsReprint);
    const clearReceipt = () => { setReceiptId(''); setSearchParams({}, { replace: true }); };
    const resetFilters = () => { setGsmId(''); setRollSize(''); setStatus('Active'); setSearchInput(''); setSearch(''); setReceivedDate(''); setNeedsReprint(false); clearReceipt(); setCurrentPage(1); };

    // --- Styling ---
    const cellStyle = { padding: '4px 5px', verticalAlign: 'middle', fontSize: '12px' };
    const cellStyleLastRow = { padding: '4px 5px', verticalAlign: 'middle',fontSize: '12px',  border:'solid 0.5px #93a89fff'  };
    const cellStyleTableHeader = { padding: '4px 5px', verticalAlign: 'middle', fontSize: '12px', border:'solid 0.5px #93a89fff' };
    const headerStyle = { ...cellStyleTableHeader, backgroundColor: '#d1e7dd', whiteSpace: 'nowrap', fontWeight: 'bold', textAlign: 'center' };

    // Sub-header style (Darker blue for the nested group header)
    const groupHeaderStyle = { ...headerStyle, backgroundColor: '#bccec6ff' };

    // --- Calculate Totals for the current page ---
    const totalIn = rolls.reduce((acc, row) => acc + (row.inQty || 0), 0);
    const totalUsed = rolls.reduce((acc, row) => acc + (row.usedQty || 0), 0);
    const totalBalance = rolls.reduce((acc, row) => acc + (row.balanceQty || 0), 0);
    const totalVal = rolls.reduce((acc, row) => acc + (row.totalValue || 0), 0);

    return (
        <>
            <style>{`
                .roll-label-print-area { display: none; }
                @media print {
                    @page { size: A4 portrait; margin: 0; }
                    .no-print { display: none !important; }
                    .roll-label-print-area { display: block; }
                    .roll-label-page {
                        width: 210mm; height: 297mm; box-sizing: border-box;
                        display: flex; flex-direction: column; align-items: center; justify-content: center;
                        page-break-after: always; break-after: page; font-family: Arial, sans-serif; color: #000;
                    }
                    .roll-label-page:last-child { page-break-after: auto; break-after: auto; }
                }
            `}</style>

            <div className="no-print">
            <NavigationBar />
            <Container fluid>
                <Row className="justify-content-center mt-4">
                    <Col md={11}>

                        {/* Page Header & Pagination */}
                        <Row className="mb-2 align-items-center">
                            <Col md="auto">
                                <h4>All Stock Roll Codes</h4>
                            </Col>
                            <Col className="d-flex justify-content-end">
                                {renderPagination({ totalPages, currentPage, setCurrentPage })}
                                {renderAdvancedPagination({ totalPages, currentPage, setCurrentPage })}
                            </Col>
                        </Row>

                        {/* Filters */}
                        <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: '8px' }}>
                            <Form.Control size="sm" style={{ width: '180px' }} placeholder="Roll code, e.g. 559"
                                value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
                            <Form.Select size="sm" style={{ width: '240px' }} value={gsmId}
                                onChange={(e) => { setGsmId(e.target.value); setRollSize(''); setCurrentPage(1); }}>
                                <option value="">All papers</option>
                                {papers.map((p) => (
                                    <option key={p.GSMID} value={p.GSMID}>{p.paperName} ({p.rolls})</option>
                                ))}
                            </Form.Select>
                            <Form.Select size="sm" style={{ width: '150px' }} value={rollSize}
                                onChange={(e) => { setRollSize(e.target.value); setCurrentPage(1); }}>
                                <option value="">All sizes</option>
                                {sizes.map((sz) => (
                                    <option key={sz.RollSize} value={sz.RollSize}>{sz.RollSize}" ({sz.rolls})</option>
                                ))}
                            </Form.Select>
                            <Form.Select size="sm" style={{ width: '130px' }} value={status}
                                onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}>
                                <option value="Active">Active</option>
                                <option value="Finished">Finished</option>
                                <option value="Returned">Returned</option>
                                <option value="">All status</option>
                            </Form.Select>
                            <Form.Control size="sm" type="date" style={{ width: '150px' }} title="Received on" value={receivedDate}
                                onChange={(e) => { setReceivedDate(e.target.value); setCurrentPage(1); }} />
                            <Form.Check type="checkbox" label="Needs reprint" checked={needsReprint}
                                onChange={(e) => { setNeedsReprint(e.target.checked); setCurrentPage(1); }} />
                            {(filtersOn || status !== 'Active') && (
                                <Button size="sm" variant="link" onClick={resetFilters}>Clear filters</Button>
                            )}
                            <span style={{ fontSize: '12px', color: '#666' }}>{totalRows} rolls · {formatNumberWithCommas(Math.round(totalKg))} kg</span>
                        </div>

                        {receiptId && (
                            <div className="d-flex align-items-center mb-2 p-2" style={{ gap: '8px', background: '#e8f5e9', borderRadius: 6, fontSize: 13 }}>
                                Showing the rolls of receipt <strong>RCV-{String(receiptId).padStart(4, '0')}</strong>
                                <Button size="sm" variant="success" disabled={preparingPrint} onClick={() => printLabels(null, { receiptId })}>Print this receipt</Button>
                                <Button size="sm" variant="link" onClick={() => navigate(`/roll-purchase?receiptId=${receiptId}`)}>Open receipt</Button>
                                <Button size="sm" variant="link" onClick={clearReceipt}>Show all rolls</Button>
                            </div>
                        )}

                        {/* Print actions */}
                        <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: '8px' }}>
                            <Button size="sm" variant="success" disabled={preparingPrint || selected.length === 0}
                                onClick={() => printLabels(selected)}>
                                {preparingPrint ? 'Preparing...' : `Print selected (${selected.length})`}
                            </Button>
                            {selected.length > 0 && (
                                <Button size="sm" variant="outline-secondary" onClick={() => setSelected([])}>Clear selection</Button>
                            )}
                            <Button size="sm" variant="outline-success" disabled={preparingPrint || activeRows === 0}
                                onClick={() => {
                                    if (activeRows > 20 && !window.confirm(`Print ${activeRows} labels (one A4 page each)?`)) return;
                                    printLabels();
                                }}>
                                {`Print all ${filtersOn ? 'filtered ' : ''}active rolls (${activeRows})`}
                            </Button>
                            <Button size="sm" variant="outline-success" disabled={preparingPrint}
                                onClick={() => printLabels(null, { receivedDate: todayBd() })}>
                                Print today's rolls
                            </Button>
                            <Button size="sm" variant="outline-danger" disabled={preparingPrint}
                                onClick={() => printLabels(null, { needsReprint: '1' })}>
                                Print labels that need reprint
                            </Button>
                            <span style={{ fontSize: '12px', color: '#666' }}>
                                One label per A4 page. Tick rolls to print just those (ticks stay while you search or change pages), or use Print on a row.
                            </span>
                        </div>

                        {/* Table */}
                        <Table bordered hover size="sm" style={{ backgroundColor: 'white' }}>
                            <thead>
                                {/* Top Header Row */}
                                <tr>
                                    <th rowSpan="2" style={{ ...headerStyle, width: '3%' }}>
                                        <Form.Check type="checkbox" checked={allPageSelected} onChange={togglePage} title="Tick all on this page" />
                                    </th>
                                    <th rowSpan="2" style={{ ...headerStyle, width: '4%' }}>Roll #</th>
                                    <th rowSpan="2" style={{ ...headerStyle, width: '9%' }}>Roll Code</th>
                                    <th rowSpan="2" style={{ ...headerStyle, width: '18%' }}>Paper Name</th>
                                    <th rowSpan="2" style={{ ...headerStyle, width: '7%' }}>Roll Size</th>

                                    {/* Spanning Header for Stock Qty */}
                                    <th colSpan="3" style={{ ...groupHeaderStyle, width: '30%' }}>Stock Qty Information</th>

                                    <th rowSpan="2" style={{ ...headerStyle, width: '8%' }}>Status</th>
                                    <th rowSpan="2" style={{ ...headerStyle, width: '14%' }}>Total Value</th>
                                    <th rowSpan="2" style={{ ...headerStyle, width: '6%' }}>Label</th>
                                </tr>

                                {/* Bottom Header Row (Nested Columns) */}
                                <tr>
                                    <th style={headerStyle}>In</th>
                                    <th style={headerStyle}>Used</th>
                                    <th style={headerStyle}>Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="11" className="text-center" style={{ padding: '20px' }}>Loading...</td></tr>
                                ) : rolls.length === 0 ? (
                                    <tr><td colSpan="11" className="text-center" style={{ padding: '20px' }}>No Data Found</td></tr>
                                ) : (
                                    <>
                                        {rolls.map((row) => (
                                            <tr key={row.rollCode} style={selected.includes(row.rollCode) ? { backgroundColor: '#e8f5e9' } : undefined}>
                                                <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                    <Form.Check type="checkbox" checked={selected.includes(row.rollCode)} onChange={() => toggleSelected(row.rollCode)} />
                                                </td>
                                                <td style={{ ...cellStyle, textAlign: 'center' }}>{row.sl}</td>
                                                <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 'bold' }}>{row.rollCode}</td>
                                                <td style={cellStyle}>{row.paperName}</td>
                                                <td style={{ ...cellStyle, textAlign: 'center' }}>{row.rollSize}"</td>

                                                {/* Qty Columns */}
                                                <td style={{ ...cellStyle, textAlign: 'right' }}>
                                                    {formatNumberWithCommas(row.inQty.toFixed(2))} kg
                                                </td>
                                                <td style={{ ...cellStyle, textAlign: 'right', color: row.usedQty > 0 ? 'red' : 'inherit' }}>
                                                    {formatNumberWithCommas(row.usedQty.toFixed(2))} kg
                                                </td>
                                                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                                    {formatNumberWithCommas(row.balanceQty.toFixed(2))} kg
                                                </td>

                                                <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                    {row.status}
                                                    {row.needsReprint && <div style={{ color: '#b02a37', fontSize: '10px', fontWeight: 'bold' }}>Reprint label</div>}
                                                </td>

                                                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                                    ৳{formatNumberWithCommas(row.totalValue.toFixed(2))}
                                                </td>
                                                <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                    <Button size="sm" variant="outline-success" style={{ padding: '0 6px', fontSize: '11px' }}
                                                        disabled={preparingPrint} onClick={() => printLabels([row.rollCode])}>
                                                        Print
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}

                                        {/* Total Row */}
                                        <tr className="roll_codes_last_row">
                                            <td colSpan="5" style={{ ...cellStyleLastRow, textAlign: 'right', fontWeight: 'bold' }}>
                                                Page Total:
                                            </td>
                                            <td style={{ ...cellStyleLastRow, textAlign: 'right', fontWeight: 'bold' }}>
                                                {formatNumberWithCommas(totalIn.toFixed(2))} kg
                                            </td>
                                            <td style={{ ...cellStyleLastRow, textAlign: 'right', fontWeight: 'bold', color: totalUsed > 0 ? 'red' : 'inherit' }}>
                                                {formatNumberWithCommas(totalUsed.toFixed(2))} kg
                                            </td>
                                            <td style={{ ...cellStyleLastRow, textAlign: 'right', fontWeight: 'bold' }}>
                                                {formatNumberWithCommas(totalBalance.toFixed(2))} kg
                                            </td>
                                            <td style={cellStyleLastRow}></td>
                                            <td style={{ ...cellStyleLastRow, textAlign: 'right', fontWeight: 'bold' }}>
                                                ৳{formatNumberWithCommas(totalVal.toFixed(2))}
                                            </td>
                                            <td style={cellStyleLastRow}></td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Container>
            </div>

            {/* Printable QR labels: roll code, paper, size and kg on top, QR (holds just the roll code) below */}
            <div className="roll-label-print-area">
                {labels.map((label) => (
                    <div key={label.RollCode} className="roll-label-page">
                        <div style={{ fontSize: '64px', fontWeight: 'bold', letterSpacing: '2px' }}>{label.RollCode}</div>
                        <div style={{ fontSize: '40px', fontWeight: 'bold', marginTop: '6mm', textAlign: 'center', padding: '0 10mm' }}>{label.paperName}</div>
                        <div style={{ fontSize: '36px', marginTop: '4mm' }}>
                            {label.RollSize}"  ·  {formatNumberWithCommas(Number(label.CurrentWeight).toFixed(0))} kg
                        </div>
                        <div style={{ marginTop: '12mm' }}>
                            <QRCode value={label.RollCode} type="svg" size={500} bordered={false} errorLevel="M" color="#000000" bgColor="#ffffff" />
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

export default RollCodes;
